/**
 * Alerts for the stable fetch loop.
 *
 * The scanner refetches every ~30 seconds, which is exactly the case the engine's de-duplication
 * exists for: without it a matching contract would alert on every pass. Rules are evaluated here,
 * at the app root, so they keep firing whichever tab is open — an alert you only receive while
 * staring at the right screen is not an alert.
 */

import React, { useEffect, useMemo, useState } from 'react';
import './AlertRulesPanel.css';
import AlertRulesPanel from './AlertRulesPanel';
import { useAlertRules, useAlertRuleStore } from '../../hooks/useAlertRules';
import { useAlertUnderlyingsFeed } from '../../hooks/useAlertUnderlyingsFeed';
import { computeMarketSignals, MARKET_SIGNALS_SOURCE, STABLE_FETCH_SOURCE } from '../../utils/alertSources';
import { markAlertsSeen, onAlertLogChange, unreadAlertCount } from '../../utils/alertRuntime';

export interface StableFetchAlertsProps {
  /** Latest rows from the stable fetch loop. */
  data: unknown[];
}

const StableFetchAlerts: React.FC<StableFetchAlertsProps> = ({ data }) => {
  const [open, setOpen] = useState(false);
  const [rules] = useAlertRuleStore();

  const tableRecords = useMemo(
    () => (Array.isArray(data) ? (data as Record<string, unknown>[]) : []),
    [data],
  );

  // A dedicated stable feed so alerts fire on their own underlyings (SPY/QQQ/SPXW +
  // whatever a rule references) regardless of what's on the table. Merged with the
  // table rows and de-duped by contract so contract-level rules see everything.
  const feedRows = useAlertUnderlyingsFeed(rules);
  const records = useMemo(() => {
    const seen = new Set<string>();
    const out: Record<string, unknown>[] = [];
    for (const r of [...feedRows, ...tableRecords]) {
      const k = `${r['Underlying']}|${r['Strike']}|${r['Option Type']}|${r['Expiry Date']}`;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(r);
    }
    return out;
  }, [feedRows, tableRecords]);
  useAlertRules(STABLE_FETCH_SOURCE, records);

  // Dataset-level signals stay scoped to the table's own rows (the user's view), so
  // the extra alert-feed underlyings don't skew the market-signals aggregates.
  const signals = useMemo(
    () => (tableRecords.length ? [computeMarketSignals(tableRecords) as Record<string, unknown>] : []),
    [tableRecords],
  );
  useAlertRules(MARKET_SIGNALS_SOURCE, signals);

  const [tab, setTab] = useState<'contracts' | 'signals'>('contracts');
  // Alerts that have fired since the pane was last opened — the number worth interrupting for.
  const [unread, setUnread] = useState<number>(() => unreadAlertCount());
  useEffect(() => onAlertLogChange((events) => setUnread(unreadAlertCount(events))), []);

  const toggle = () => {
    setOpen((wasOpen) => {
      if (!wasOpen) {
        markAlertsSeen();
        setUnread(0);
      }
      return !wasOpen;
    });
  };
  const mine = rules.filter(
    (r) => r.sourceId === STABLE_FETCH_SOURCE.id || r.sourceId === MARKET_SIGNALS_SOURCE.id,
  );
  const activeCount = mine.filter((r) => r.enabled).length;
  const shownSource = tab === 'signals' ? MARKET_SIGNALS_SOURCE : STABLE_FETCH_SOURCE;

  return (
    <>
      <button
        type="button"
        className="mmecStableAlertsBtn"
        onClick={toggle}
        title={`Alerts${activeCount ? ` — ${activeCount} rule${activeCount === 1 ? '' : 's'} active` : ''}${
          unread ? `, ${unread} new` : ''
        }`}
      >
        🔔 <span className="mmecStableAlertsLabel">Alerts</span>
        {unread > 0 && (
          <span className="mmecAlertBadge" aria-label={`${unread} new alerts`}>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="mmecAlertDrawer">
          <div className="arSourceTabs">
            <button type="button" className={`chip${tab === 'contracts' ? ' on' : ''}`} onClick={() => setTab('contracts')}>
              Contracts
            </button>
            <button type="button" className={`chip${tab === 'signals' ? ' on' : ''}`} onClick={() => setTab('signals')}>
              Market signals
            </button>
          </div>
          <AlertRulesPanel source={shownSource} onClose={() => setOpen(false)} />
        </div>
      )}
    </>
  );
};

export default StableFetchAlerts;
